/**
 PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA

 *  Copyright © 2017 Pearson Education, Inc.
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
* */

import LruCache from 'lru-cache-promise';

/**
 * A class for caching request. This works as a caching wrapper on the client.
 *
 * @author Hari Gangadharan
 */
export default class CacheEnabledClient {
  /**
   * Creates this.
   *
   * @param client the data fetch client
   * @param cacheOptions the cache options
   */
  constructor(client, cacheOptions) {
    this.client = client;
    this.cache = new LruCache(cacheOptions || {
      max: 10,
      maxAge: 2 * 60 * 60 * 1000
    });
  }

  /**
   * Prefetches the data for the id.
   *
   * @param id the id to fetch.
   */
  prefetch(id, contentType) {
    if (!this.cache.has(id)) {
      this.get(id, contentType).then(
        () => { /* ignored */ },
        () => { /* ignored */ }
      );
    }
  }

  /**
   * Fetch HTML Page content via API and assign in cache.
   *
   * @param id the id to fetch.
   */

  fetchFunction =id => new Promise((resolve, reject) => {
    this.client.get(id).then(
      (response) => {
        this.cache.set(id, response);
        resolve(response);
      },
      (error) => {
        reject(error);
      }
    );
  })

  /**
   * Gets the data for the id. If data is available in cache, returns from cache; otherwise fetches the data from
   * the client.
   *
   * @param id the id to fetch
   * @returns {Promise} to the data
   */
  fetchPage(id) {
    return new Promise((resolve, reject) => {
      const cachedResponse = this.cache.get(id);
      if (cachedResponse) {
        resolve(cachedResponse);
      } else {
        this.client.get(id).then(
          (response) => {
            this.cache.set(id, response);
            resolve(response);
          },
          (error) => {
            reject(error);
          }
        );
      }
    });
  }

  /**
   * Gets the data as async for the id. If data is available in cache, returns from cache; otherwise fetches the data from
   * the client.
   *
   * @param id the id to fetch
   * @returns {Promise} to the data
   */
  get(id, contentType) {
    return new Promise((resolve) => {
      if (contentType && contentType.toLowerCase() === 'cite') {
        this.cache.getAsync(id, this.fetchFunction).then((value) => {
          resolve(value);
        });
      } else {
        this.fetchPage(id).then((response) => {
          resolve(response);
        });
      }
    });
  }
}



// WEBPACK FOOTER //
// ./src/js/util/CacheEnabledClient.js